import classNames from 'classnames';
import styles from './VideoCard.module.scss';
import { ConsentPlaceholder, Heading, Player, Link, LinkButton } from 'ui/components';
import IconArrowRight from 'ui/icons/icon-arrow-right-web.svg';
import { useConsent, YOUTUBE_REQUIRED_GROUPS } from 'application/adapters/context/ConsentContext';
import { isYouTube } from 'helpers/youtubeEmbed';

export interface VideoCardProps {
	className?: string;
	poster?: Umbraco.Image;
	kicker?: string;
	heading?: string;
	text?: string;
	anchor?: string;
	mediaURL: string;
	orientation: Umbraco.Orientation;
	disableLightPlayer: boolean;
	playing: boolean;
	loop: boolean;
	controls: boolean;
	noGradient?: boolean;
	link: Umbraco.Link;
	variant?: 'default' | 'website';
	displayLinkAsButton?: boolean;
	consentHeading: string;
	consentText: string;
}

export const VideoCard: React.FC<VideoCardProps> = ({
	className,
	poster,
	mediaURL,
	kicker,
	heading,
	text,
	orientation,
	disableLightPlayer,
	playing,
	loop,
	controls,
	noGradient,
	anchor,
	link,
	displayLinkAsButton = false,
	variant = 'default',
	consentHeading,
	consentText,
}) => {
	const hasYouTubeConsent = useConsent(YOUTUBE_REQUIRED_GROUPS);

	// Render CTA based on settings
	const renderCTA = () => {
		if (!link) return null;
		const commonProps = {
			url: link?.url,
			target: link?.target,
			className: styles.VideoCard_link,
			children: link?.name,
		};

		if (!displayLinkAsButton) {
			return <Link chevron="after" variant={variant === 'website' ? 'website' : 'default'} {...commonProps} />;
		} else {
			return (
				<LinkButton
					style={variant === 'website' ? 'secondaryWeb' : 'secondary'}
					{...(variant === 'website' ? { withBackIcon: <IconArrowRight /> } : {})}
					{...commonProps}
				/>
			);
		}
	};

	return (
		<div
			{...(anchor && { id: anchor })}
			className={classNames(
				styles.VideoCard,
				{ 'u-bottom-padding--md': variant === 'default' },
				{ [styles.VideoCard___row]: orientation === 'Horizontal' },
				{ [styles.VideoCard___rowReverse]: orientation === 'Horizontal Reverse' },
				{ [styles.VideoCard___website]: variant === 'website' },
				{ [styles.VideoCard___noGradient]: noGradient },
				className,
			)}
		>
			<div className={styles.VideoCard_media}>
				<span className={styles.VideoCard_mediabox}>
					{/* Don't mount Player without consent, it preloads noembed, i.ytimg and iframe_api */}
					{isYouTube(mediaURL) && !hasYouTubeConsent ? (
						<ConsentPlaceholder heading={consentHeading} text={consentText} />
					) : (
						<Player
							url={mediaURL}
							{...(poster ? { poster: poster?.url } : {})}
							isLight={!disableLightPlayer}
							playing={playing}
							loop={loop}
							controls={!controls}
						/>
					)}
				</span>
			</div>

			<div className={styles.VideoCard_textbox}>
				{variant === 'default' && (
					<>
						{kicker && <p className={styles.VideoCard_kicker}>{kicker}</p>}
						{heading && <Heading headingLevel="h3">{heading}</Heading>}
					</>
				)}
				{/* Website version */}
				{variant === 'website' && (
					<>
						{heading && (
							<Heading headingLevel="h2" style="displaySm" className={styles.VideoCard_heading}>
								{heading}
							</Heading>
						)}
						{kicker && <p className={styles.VideoCard_kicker}>{kicker}</p>}
						{text && <p className={styles.VideoCard_text}>{text}</p>}
					</>
				)}
				{/* CTA */}
				{renderCTA()}
			</div>
		</div>
	);
};
